import React, { useState } from 'react';

const FSSAILicense = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with FSSAI License registration.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with FSSAI License registration.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-16">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              FSSAI License & Registration
            </h1>
            <p className="mt-4 text-xl text-blue-100">
              Mandatory authorization for all Food Business Operators (FBOs) in India as per the FSS Act, 2006.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">📋</span> Start Your Application Today
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Our Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">Documents Required</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#deliverables">Deliverables</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#faqs">Common Questions</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#penalties">Penalties & Renewal</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview & Benefits
              </h2>

              <h3 className="text-2xl font-bold text-blue-600 mb-4">
                FSSAI License: The Legal Mandate and Types
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                As stipulated by <strong>Section 31(1) of the Food Safety and Standards Act, 2006</strong>, it is mandatory for all <strong>Food Business Operators (FBOs)</strong> in India to obtain an FSSAI license or registration from the Food Safety & Standards Authority of India (FSSAI).
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Any entity involved in the manufacturing, storage, transportation, or distribution of food, including small hawkers and petty retailers, must hold a valid FSSAI Authorization. The specific type of authorization depends on your scale of operation:
              </p>
              <div className="mt-8 space-y-4">
                <h3 className="text-2xl font-bold text-blue-600 mb-4"> The Three Categories of FSSAI Authorization </h3>
                <ul className="space-y-4 text-gray-700">
                  <li className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">👤</span>
                    <div>
                      <strong className="font-semibold">1. Basic FSSAI Registration:</strong> For FBOs with an annual turnover <strong>up to Rs. 12 Lakhs</strong>. Granted by the respective <strong>State Government</strong>.
                    </div>
                  </li>
                  <li className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">🏢</span>
                    <div>
                      <strong className="font-semibold">2. State FSSAI License:</strong> For FBOs with an annual turnover <strong>exceeding Rs. 12 Lakhs but not exceeding Rs. 20 Crores</strong>. Granted by the respective <strong>State Government</strong>.
                    </div>
                  </li>
                  <li className="p-4 border border-gray-200 rounded-lg bg-gray-50 flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">🏛️</span>
                    <div>
                      <strong className="font-semibold">3. Central FSSAI License:</strong> Mandatory for businesses with an annual turnover <strong>exceeding Rs. 20 Crores</strong>, or those involved in importing/exporting, or operating in multiple states. Granted by the <strong>Central Government</strong>.
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">👨‍🍳</span>
                    <div>
                      <strong className="font-semibold">Legal Compliance:</strong> It's a statutory requirement, securing your business from legal penalties and fines.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">🛡️</span>
                    <div>
                      <strong className="font-semibold">Consumer Trust:</strong> The FSSAI logo is a mark of quality and safety, building confidence and goodwill among consumers.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">📈</span>
                    <div>
                      <strong className="font-semibold">Business Expansion:</strong> Facilitates expansion, loan approvals, and permits you to obtain other necessary government licenses.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">📊</span>
                    <div>
                      <strong className="font-semibold">Regulatory Monitoring:</strong> Helps the regulator maintain standards and provides the FBO with guidelines for food safety management.
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-32">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Get Your FSSAI License Today
                </h3>
                <p className="text-gray-600 mb-6">
                  Our experts handle the entire application process, ensuring compliance and quick approval.
                </p>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with FSSAI License registration." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">📨</span>
                        Request an Initial Consultation
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Procedure Section */}
      <section id="procedure" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Our Detailed FSSAI License Application Process
          </h2>
          <p className="text-center text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Akir Advisory follows a seamless, four-step digital process to ensure your FSSAI registration is obtained quickly and without errors.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: "01", icon: "📋", title: "Requirement Analysis & Document Collection", desc: "We assess your business scale (turnover/operations) to determine the correct FSSAI category (Basic, State, or Central). You submit the scanned copies of required documents online." },
              { number: "02", icon: "📤", title: "Application Drafting & Submission", desc: "Our experts draft the online application, ensure the declaration forms are compliant, and submit the application (Form A or Form B) to the FSSAI portal." },
              { number: "03", icon: "💬", title: "Follow-up & Query Resolution", desc: "We continuously monitor the application status and respond to any queries raised by the Food Safety Officer on your behalf to minimize delays." },
              { number: "04", icon: "🏆", title: "License Issuance & Delivery", desc: "Once approved, the FSSAI License/Registration Certificate is issued. We deliver the digital certificate and its related documents to you." }
            ].map((step, index) => (
              <div key={index} className="bg-blue-50 p-6 rounded-xl shadow-lg border-t-4 border-blue-600 flex flex-col h-full">
                <div className="procedure-step-content">
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold text-blue-600 block mb-2">{step.number}</span>
                    <span className="text-2xl block mb-2">{step.icon}</span>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-700 text-sm">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Documents Required for FSSAI Application
          </h2>
          <p className="text-center text-lg text-gray-600 max-w-3xl mx-auto mb-10">
            Only Scanned Copies are required
          </p>
          <p className="text-lg text-gray-700 mb-8">
            The documents vary slightly based on the type of license (Basic, State, or Central), but the common requirements are listed below.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: "01", icon: "📍", title: "Proof of Address", desc: "Rent Agreement, Utility Bills (Electricity/Water), or Sale Deed for the business premises." },
              { number: "02", icon: "🆔", title: "Identity Proof", desc: "Passport-size photos, Aadhaar Card, PAN Card of the Proprietor/Partner/Director." },
              { number: "03", icon: "📄", title: "Business Constitution Proof", desc: "Partnership Deed, Certificate of Incorporation (for Company/LLP), or Memorandum/Articles of Association (MOA/AOA)." },
              { number: "04", icon: "📊", title: "Food Safety Management System Plan", desc: "For State/Central Licenses, a detailed plan or declaration is required." }
            ].map((doc, index) => (
              <div key={index} className="bg-blue-50 p-6 rounded-xl shadow-lg border-t-4 border-blue-600 flex flex-col h-full">
                <div className="procedure-step-content">
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold text-blue-600 block mb-2">{doc.number}</span>
                    <span className="text-2xl block mb-2">{doc.icon}</span>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{doc.title}</h3>
                    <p className="text-gray-700 text-sm">{doc.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section id="deliverables" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Your Deliverables: What Akir Advisory Provides
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to be a legally compliant Food Business Operator in India.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "🏆", title: "FSSAI License/Registration Certificate", desc: "The official government certificate permitting your food operations." },
              { icon: "✅", title: "Form B or Form A Application", desc: "The final application form submitted to the FSSAI portal for your records." },
              { icon: "🛡️", title: "Food Safety Management System (FSMS) Declaration", desc: "Mandatory declaration/plan required for State and Central licenses." },
              { icon: "📋", title: "Checklist & Compliance Advisory", desc: "Advisory on annual compliance requirements and display guidelines." }
            ].map((item, index) => (
              <div key={index} className="p-6 bg-blue-50 rounded-xl shadow-lg border-l-4 border-blue-600 flex items-start space-x-4">
                <span className="text-2xl flex-shrink-0 mt-1">{item.icon}</span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Penalties Section */}
      <section id="penalties" className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Penalties for Non-Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 bg-white rounded-xl shadow-lg border-l-4 border-red-500">
              <span className="text-2xl mb-4 block">❌</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Operating Without a License</h3>
              <p className="text-gray-600">
                Any FBO found operating without a valid FSSAI license is liable to imprisonment for a term up to <strong>six months</strong> and a fine which may extend up to <strong>₹5 Lakhs</strong>.
              </p>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg border-l-4 border-red-500">
              <span className="text-2xl mb-4 block">⚠️</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Substandard Food Quality</h3>
              <p className="text-gray-600">
                Penalties for selling or distributing substandard food can range from fines up to <strong>₹5 Lakhs</strong> to imprisonment, depending on the severity of the offense.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Common Questions About FSSAI Licensing
            </h2>
            <p className="text-lg text-gray-600">
              Get quick answers to the most frequent queries from Food Business Operators.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-3 text-blue-600">❓</span>
                  How long is the FSSAI license valid?
                </h3>
                <p className="text-gray-600 text-sm">
                  The license or registration is issued for a period ranging from <strong>1 year to 5 years</strong>. You must apply for renewal at least 30 days before its expiry.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-3 text-blue-600">❓</span>
                  Is FSSAI license mandatory for home-based food businesses?
                </h3>
                <p className="text-gray-600 text-sm">
                  Yes. Even small-scale or home-based FBOs with an annual turnover up to ₹12 Lakhs must obtain the <strong>Basic FSSAI Registration</strong>.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-3 text-blue-600">❓</span>
                  What is the difference between State and Central License?
                </h3>
                <p className="text-gray-600 text-sm">
                  The main difference is based on turnover. <strong>Central License</strong> is for businesses with a turnover above ₹20 Crores or those involved in import/export. <strong>State License</strong> is for those with turnover between ₹12 Lakhs and ₹20 Crores.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-3 text-blue-600">❓</span>
                  What if I miss the renewal deadline?
                </h3>
                <p className="text-gray-600 text-sm">
                  Failing to renew before the expiry date incurs a late fee of <strong>₹100 per day</strong> and your license may be cancelled if the renewal is delayed significantly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-10 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
           Ready to secure your FSSAI Authorization?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Let our team of advisors simplify your FSSAI compliance journey.
          </p>
          <a href="#contact-section" className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-600 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
              <span className="mr-2">📞</span> Consult Our Expert Today
          </a>
        </div>
      </section>
    </main>
  );
};

export default FSSAILicense;